import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  isCommitNotOnBranchError,
  useRestoreBranchMutation,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { newOperationKey } from './newOperationKey'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'

export interface BranchRestoreDialogProps {
  branch: Branch
  /**
   * Commit to restore to; omitted = the branch head, i.e. "discard
   * uncommitted changes".
   */
  commit?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Confirm dialog for restoring a branch to a commit on its history. For an
 * unprotected branch this rebuilds the working copy in place (discard
 * uncommitted changes when no commit is given, or undo back to an earlier
 * commit) — the head pointer never moves. For a protected branch (which can't
 * be rewound) the server checks the commit out into a new "restore-to-<commit>"
 * branch and the dialog switches to it.
 */
export function BranchRestoreDialog({
  branch,
  commit,
  open,
  onOpenChange
}: BranchRestoreDialogProps) {
  const [restoreBranch, restoreState] = useRestoreBranchMutation()
  const { switchBranch } = useSwitchBranch()
  const [submitError, setSubmitError] = React.useState('')
  const restoreKey = React.useRef<string | null>(null)
  const [lastCommit, setLastCommit] = React.useState<string | undefined>(commit)
  const activeCommit = commit ?? lastCommit
  const short = activeCommit?.slice(0, 8)
  const protectedSource = branch.protected

  React.useEffect(() => {
    if (commit !== undefined) {
      setLastCommit(commit)
    }
  }, [commit])

  React.useEffect(() => {
    restoreKey.current = null
  }, [branch.id, activeCommit])

  React.useEffect(() => {
    if (!open) {
      setSubmitError('')
      restoreKey.current = null
      restoreState.reset()
    }
  }, [open, restoreState])

  const submitting = restoreState.isLoading

  const handleRestore = async () => {
    if (submitting) return
    setSubmitError('')
    restoreKey.current ??= newOperationKey()
    try {
      const result = await restoreBranch({
        id: branch.id,
        idempotencyKey: restoreKey.current,
        ...(activeCommit ? { commit: activeCommit } : {})
      }).unwrap()
      // A protected restore returns a fresh branch rather than rewinding the
      // source — switch onto it so the user lands on the restored state.
      if (result.id !== branch.id) {
        await switchBranch(result.id)
        toast.success(`Created ${result.name} and switched to it`)
      } else {
        toast.success(
          short
            ? `Restored ${branch.name} to ${short}`
            : `Discarded uncommitted changes on ${branch.name}`
        )
      }
      onOpenChange(false)
    } catch (err) {
      setSubmitError(extractRestoreError(err))
    }
  }

  const title = short ? `Restore to ${short}?` : 'Discard uncommitted changes?'
  const confirmLabel = short ? 'Restore' : 'Discard changes'

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {protectedSource ? (
              <>
                {branch.name} is protected and can&apos;t be rewound, so this creates a new branch
                loaded with{' '}
                {short ? <span className="font-mono text-ink">{short}</span> : 'this commit'}
                &apos;s state and switches you to it. {branch.name}&apos;s history is untouched.
              </>
            ) : short ? (
              <>
                This discards everything after <span className="font-mono text-ink">{short}</span>{' '}
                on this branch&apos;s working copy — committed history is kept.
              </>
            ) : (
              <>
                This rebuilds {branch.name}&apos;s working copy from its last commit. Everything not
                committed is discarded — committed history is kept.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {submitError ? (
          <p role="alert" className="text-sm text-st-down">
            {submitError}
          </p>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <Button variant="destructive" onClick={handleRestore} disabled={submitting}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function extractRestoreError(err: unknown): string {
  if (isCommitNotOnBranchError(err)) {
    return "This commit is not on the branch's history."
  }
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) return message
    }
  }
  return 'Could not restore the branch.'
}
